import { createApp } from "vue";
import { createPinia } from "pinia";
import Notifications from "@kyvg/vue3-notification";
import NProgress from "nprogress";
import "nprogress/nprogress.css";

import App from "./App.vue";
import router from "./router";
import { useAuthStore } from "./stores/auth";
import { applyColorMode, getStoredColorModePreference } from "./lib/theme";
import { useThemeStore } from "./stores/theme";
import http, { setAuthToken } from "./api/http";
import { ovReq } from "./api/ov-req";
import { ovData } from "./lib/ov-data";
import { notify } from "./composables/useNotify";
import { toCurrency } from "./utils/format";
import { tooltipDirective } from "./design-system/directives/tooltip";

import OvButton from "./design-system/components/OvButton.vue";
import OvBadge from "./design-system/components/OvBadge.vue";
import OvCheckbox from "./design-system/components/OvCheckbox.vue";
import OvTableCheck from "./design-system/components/OvTableCheck.vue";
import OvInput from "./design-system/components/OvInput.vue";
import OvSelect from "./design-system/components/OvSelect.vue";
import OvInputGroup from "./design-system/components/OvInputGroup.vue";
import OvDropdown from "./design-system/components/OvDropdown.vue";
import OvDropdownItem from "./design-system/components/OvDropdownItem.vue";
import OvCard from "./design-system/components/OvCard.vue";
import OvEmptyState from "./design-system/components/OvEmptyState.vue";
import OvTableSkeleton from "./design-system/components/OvTableSkeleton.vue";
import OvKpiCard from "./design-system/components/OvKpiCard.vue";
import OvPageHeader from "./design-system/components/OvPageHeader.vue";
import LoadingOverlay from "./design-system/components/LoadingOverlay.vue";
import OvBusySpinner from "./design-system/components/OvBusySpinner.vue";

import SortColumn from "./components/SortColumn.vue";
import Paginate from "./components/Paginate.vue";
import QuickEdit from "./components/QuickEdit.vue";
import QuickEditPayout from "./components/QuickEditPayout.vue";
import ValidatedInput from "./components/ValidatedInput.vue";
import DocList from "./components/form/DocList.vue";
import OvDateTimePicker from "./components/OvDateTimePicker.vue";

import "./design-system/base.scss";
import "./styles/style.scss";

NProgress.configure({ showSpinner: false, trickleSpeed: 200 });

const envName = import.meta.env.VUE_APP_ENV_NAME || "ovalio-media";

async function init() {
	applyColorMode(getStoredColorModePreference());

	const pinia = createPinia();
	const app = createApp(App);

	app.use(pinia);
	useThemeStore(pinia);
	app.use(router);
	app.use(Notifications);

	app.component("OvButton", OvButton);
	app.component("OvBadge", OvBadge);
	app.component("OvCheckbox", OvCheckbox);
	app.component("OvTableCheck", OvTableCheck);
	app.component("OvInput", OvInput);
	app.component("OvSelect", OvSelect);
	app.component("OvInputGroup", OvInputGroup);
	app.component("OvDropdown", OvDropdown);
	app.component("OvDropdownItem", OvDropdownItem);
	app.component("OvCard", OvCard);
	app.component("OvEmptyState", OvEmptyState);
	app.component("OvTableSkeleton", OvTableSkeleton);
	app.component("OvKpiCard", OvKpiCard);
	app.component("OvPageHeader", OvPageHeader);
	app.component("loading", LoadingOverlay);
	app.component("OvBusySpinner", OvBusySpinner);
	app.component("ov-th", SortColumn);
	app.component("paginate", Paginate);
	app.component("quick-edit", QuickEdit);
	app.component("quick-edit-payout", QuickEditPayout);
	app.component("validated-input", ValidatedInput);
	app.component("doc-list", DocList);
	app.component("ov-date-time-picker", OvDateTimePicker);

	app.directive("tooltip", tooltipDirective);

	app.config.globalProperties.$ovReq = ovReq;
	app.config.globalProperties.$ovData = ovData;
	app.config.globalProperties.$ovNotify = notify;
	app.config.globalProperties.$toCurrency = toCurrency;
	app.config.globalProperties.$envName = envName;

	const authStore = useAuthStore(pinia);
	let token = localStorage.getItem("authToken");

	if (token) {
		try {
			setAuthToken(token);
			const resp = await http.get("user/getCurrent");
			if (resp.data.err === 0) {
				authStore.setUser({
					user: {
						...resp.data.data.user,
						config: resp.data.data.config,
					},
					token,
				});
			} else {
				token = null;
			}
		} catch (e) {
			token = null;
			authStore.setUser(null);
		}
	}

	router.beforeEach((to, from, next) => {
		authStore.closeSidebar();
		NProgress.start();

		if (to.name === "login") {
			if (authStore.user) {
				const user = authStore.user;
				const routeName = user.type === "advertiser" || (user.type === "admin" && user.advertiser_id)
					? "dashboard"
					: "statistics";
				next({ name: routeName });
				return;
			}
		} else if (!authStore.user) {
			next({ name: "login" });
			return;
		} else if (to.name === "dashboard" || to.name === "dashboard-edit") {
			const user = authStore.user;
			const isAdvertiser = user.type === "advertiser" || (user.type === "admin" && user.advertiser_id);
			if (!isAdvertiser) {
				next({ name: "statistics" });
				return;
			}
		}
		next();
	});

	router.afterEach(() => {
		NProgress.done();
	});

	app.config.globalProperties.$authStore = authStore;

	app.mount("#app");
}

init();
